import { motion, AnimatePresence } from "framer-motion";
import { Shield, CheckCircle, PartyPopper, Sparkles } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import InlineVerificationChecklist from "@/components/shared/InlineVerificationChecklist";
import { useOfferDetail } from "./OfferDetailContext";

export default function OfferVerificationSection() {
  const {
    offer, cc, user, tracker, userHasVerified, submitting,
    showCelebration, checklistSteps, setChecklistSteps,
    handleVerify, setVerifyResult, setShowVerifyModal,
    setPriceReportStep, setPrActualValue, setPrLocationCity, setPrWhatReceived, setPrNotes,
    getText,
  } = useOfferDetail();

  return (
    <>
      {/* Inline Verification Checklist — Accordion */}
      <div style={{ marginTop: 'var(--card-gap, 12px)', marginBottom: 'var(--card-gap, 12px)' }}>
        <Accordion type="single" collapsible className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <AccordionItem value="verification" className="border-0">
            <AccordionTrigger className="px-4 py-3 hover:no-underline" data-testid="verification-checklist-trigger">
              <span className="flex items-center gap-2 font-semibold text-slate-800 text-sm">
                <Shield className="w-4 h-4" style={{ color: cc.hex || "#3b82f6" }} />
                Verification Checklist
                {userHasVerified && <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />}
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-1">
              <InlineVerificationChecklist
                steps={checklistSteps}
                onToggle={(key) => setChecklistSteps(prev => ({ ...prev, [key]: !prev[key] }))}
                onSubmit={async (verdict) => {
                  const submitted = await handleVerify(verdict);
                  if (submitted === "confirmed" || submitted === "changed") {
                    setPrActualValue(offer.offer_value_est > 0 ? String(offer.offer_value_est) : "");
                    setPrLocationCity(user?.city || "");
                    setPrWhatReceived(submitted === "confirmed" ? "Exactly as described" : "");
                    setPrNotes("");
                    setPriceReportStep(true);
                    setShowVerifyModal(true);
                  }
                }}
                userHasVerified={userHasVerified}
                submitting={submitting}
                accentColor={cc.hex || "#3b82f6"}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Celebration Animation */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            data-detail-card
            className="bg-gradient-to-r from-amber-50 to-green-50 border border-amber-200 text-center"
            data-testid="celebration-card"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <PartyPopper className="w-6 h-6 text-amber-500" />
              <span className="text-lg font-bold text-slate-800">You're awesome!</span>
              <Sparkles className="w-6 h-6 text-amber-500" />
            </div>
            <p className="text-sm text-slate-600">
              Your verification helps the community trust this deal. Keep it up!
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Soft Reminder */}
      {offer.offer_requires_app && tracker?.claimed && !userHasVerified && !showCelebration && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          data-detail-card
          className="bg-gradient-to-r from-sky-50 to-indigo-50 border border-sky-200"
          data-testid="soft-reminder-card"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-sky-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-800" data-testid="soft-reminder-text">{getText("confirm_offer")}</p>
              <p className="text-xs text-slate-500 mt-0.5">You claimed this deal. Verify it to help others and earn 15 pts!</p>
              <button
                onClick={() => {
                  setVerifyResult("confirmed");
                  setShowVerifyModal(true);
                }}
                className="mt-2 px-4 py-2 rounded-xl bg-sky-500 text-white text-xs font-semibold shadow-md hover:bg-sky-600 transition-colors"
                data-testid="soft-reminder-verify-btn"
              >
                Yes, I got it — Verify!
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
}
